import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import PageHeader from '../components/ui/PageHeader';

import { jsPDF } from "jspdf";
import "jspdf-autotable";

export const PADConsulta = () => {
    const { axiosInstance } = useAuth();
    const [filtro, setFiltro] = useState('');
    const [resultados, setResultados] = useState(null);
    const [loading, setLoading] = useState(false);
    const toast = useRef(null);

    const realizarBusqueda = async (e) => {
        if (e) e.preventDefault();

        if (!filtro.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'Ingrese un criterio de búsqueda' });
            return;
        }

        setLoading(true);
        try {
            const response = await axiosInstance.get("pad-expediente/buscar-concluidos", {
                params: {
                    filtro: filtro,
                    page: 0,
                    size: 50
                }
            });
            const soloConcluidos = response.data.content.filter(
                exp => exp.estado === 'CONCLUIDO'
            );
            setResultados(soloConcluidos);
        } catch (error) {
            console.error(error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Error al consultar el servidor' });
        } finally {
            setLoading(false);
        }
    };

    const limpiarBusqueda = () => {
        setFiltro('');
        setResultados(null);
    };

    const personaBody = (rowData) => (
        <span>{`${rowData.persona.nombre} ${rowData.persona.apellido}`} <br />
            <small className="text-muted">{rowData.persona.docIdentidad}</small></span>
    );

    const generarPDF = (persona, expedientes) => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const yBase = 10;

        // 1. Escudo Perú - IZQUIERDA (Ancho 14, Alto 18)
        // Posición x: 20 (margen estándar)
        doc.addImage('/logos/escudoperu.png', 'PNG', 20, yBase, 14, 18);

        // 2. Poder Judicial - CENTRO (Ancho 28, Alto 18)
        // Cálculo para centrar: (AnchoPágina / 2) - (AnchoLogo / 2)
        const centerX = (pageWidth / 2) - (28 / 2);
        doc.addImage('/logos/pjudicial.png', 'PNG', centerX, yBase, 28, 18);

        // 3. Bicentenario - DERECHA (Ancho 19, Alto 12)
        // Posición x: AnchoPágina - margen(20) - AnchoLogo(19)
        // Alineación vertical: Se le suma 3 a yBase para que el logo de 12px quede centrado respecto a los de 18px
        const rightX = pageWidth - 20 - 19;
        doc.addImage('/logos/bicentenario.jpg', 'JPG', rightX, yBase + 3, 19, 12);

        // --- ENCABEZADO ---
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.text("Presidencia de la Corte Superior de Justicia de La Libertad", pageWidth / 2, 34, { align: "center" });
        doc.text("Gerencia de Administración Distrital", pageWidth / 2, 38, { align: "center" });

        // --- TÍTULO ---
        doc.setFontSize(13.5);
        doc.setFont("helvetica", "bold");

        const titulo = "CONSTANCIA DE MEDIDAS DISCIPLINARIAS";
        const xCentro = pageWidth / 2;
        const yTitulo = 52;

        doc.text(titulo, xCentro, yTitulo, { align: "center" });

        // --- LÓGICA DE SUBRAYADO ---
        const textWidth = doc.getTextWidth(titulo);
        const xInicio = xCentro - (textWidth / 2);
        const xFin = xCentro + (textWidth / 2);
        const yLinea = yTitulo + 1.5;

        doc.setLineWidth(0.5);
        doc.line(xInicio, yLinea, xFin, yLinea);


        // --- CONFIGURACIÓN DE MÁRGENES Y ANCHO ---
        const marginX = 25;
        const maxWidth = pageWidth - (marginX * 2); // 210 - 50 = 160mm de área de texto

        // --- CUERPO ---
        doc.setFontSize(11);
        doc.setFont("helvetica", "normal");
        const textoPresentacion = "Quien suscribe, Gerente de Administración Distrital de la Corte Superior de Justicia de La Libertad.";
        doc.text(textoPresentacion, 25, 65, {
            maxWidth: maxWidth,
            align: "justify",
            lineHeightFactor: 1.5 // Aumenta el espacio entre líneas (1.5 es un estándar cómodo)
        });

        doc.setFont("helvetica", "bold");
        doc.text("HACE CONSTAR;", 25, 80);

        doc.setFont("helvetica", "normal");
        const tieneSanciones = expedientes && expedientes.length > 0;

        let mensajePrincipal = `Que, el señor(a) ${persona.nombre.toUpperCase()} ${persona.apellido.toUpperCase()}, identificado con Documento Nacional de Identidad N° ${persona.docIdentidad}, quien presta servicios en esta Corte Superior de Justicia, asignado al Registro Distrital Judicial, en la búsqueda de legajos en el área de Recursos Humanos, y lo informado por la Secretaría Técnica de Procedimientos Administrativos Disciplinarios: `;

        if (!tieneSanciones) {
            mensajePrincipal += "En la búsqueda de legajos en el área de Recursos Humanos y la Secretaría Técnica: NO REGISTRA Sanción Disciplinaria Administrativa alguna.";
        } else {
            mensajePrincipal += "Registra las siguientes Medidas Disciplinarias Administrativas concluidas:";
        }

        doc.text(mensajePrincipal, 25, 88, {
            maxWidth: maxWidth,      // Asegúrate que maxWidth sea aprox 160 (210 - 25 - 25)
            align: "justify",
            lineHeightFactor: 1.5
        });

        // --- TABLA (Si tiene sanciones) ---
        if (tieneSanciones) {
            doc.autoTable({
                startY: 125,
                head: [['N° Expediente', 'Sanción Impuesta', 'Fecha Registro']],
                body: expedientes.map(exp => [
                    exp.expediente,
                    exp.sancion,
                    new Date(exp.fecRegistro).toLocaleDateString()
                ]),
                theme: 'grid',
                headStyles: {
                    fillColor: [142, 25, 30], // color PJ institucional
                    //halign: 'center'
                },
                styles: {
                    fontSize: 10,
                    cellPadding: 3
                },
                columnStyles: {
                    0: { cellWidth: 40 },
                    2: { halign: 'center' }
                },
                margin: { left: 25, right: 25 }
            });
        }

        // --- PIE DE PÁGINA ---
        const finalY = tieneSanciones ? doc.lastAutoTable.finalY + 20 : 110;

        doc.text("Se expide el presente documento a solicitud del interesado para los fines que estime conveniente.", 25, finalY, {
            maxWidth: maxWidth,
            align: "justify",
            lineHeightFactor: 1.5
        });

        const fechaActual = `Trujillo, ${new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}`;
        doc.text(fechaActual, 25, finalY + 30);

        doc.setFontSize(8);
        doc.text("Nota: Se han considerado los últimos seis meses.", 25, finalY + 45);

        doc.save(`Constancia_PAD_${persona.docIdentidad}.pdf`);
    };

    // --- CONSTANCIA SIMPLE ---
    const generarConstanciaSimple = (persona) => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const marginX = 25;
        const maxWidth = pageWidth - (marginX * 2);

        // 1. Logos (Reutilizamos tu lógica de posicionamiento)
        doc.addImage('/logos/escudoperu.png', 'PNG', 20, 10, 14, 18);
        const centerX = (pageWidth / 2) - (28 / 2);
        doc.addImage('/logos/pjudicial.png', 'PNG', centerX, 10, 28, 18);
        const rightX = pageWidth - 20 - 19;
        doc.addImage('/logos/bicentenario.jpg', 'JPG', rightX, 13, 19, 12);

        // 2. Encabezado institucional
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.text("Presidencia de la Corte Superior de Justicia de La Libertad", pageWidth / 2, 34, { align: "center" });
        doc.text("Gerencia de Administración Distrital", pageWidth / 2, 38, { align: "center" });

        // 3. Título con subrayado
        doc.setFontSize(13.5);
        doc.setFont("helvetica", "bold");
        const titulo = "CONSTANCIA DE MEDIDAS DISCIPLINARIAS";
        doc.text(titulo, pageWidth / 2, 52, { align: "center" });
        const textWidth = doc.getTextWidth(titulo);
        doc.line((pageWidth / 2) - (textWidth / 2), 53.5, (pageWidth / 2) + (textWidth / 2), 53.5);

        // 4. Cuerpo del documento 
        doc.setFontSize(11);
        doc.setFont("helvetica", "normal");
        doc.text("Quien suscribe, Gerente de Administración Distrital de la Corte Superior de Justicia de La Libertad.", marginX, 65, {
            maxWidth: maxWidth,
            align: "justify",
            lineHeightFactor: 1.5
        });

        doc.setFont("helvetica", "bold");
        doc.text("HACE CONSTAR;", marginX, 80);

        doc.setFont("helvetica", "normal");
        const mensajePrincipal = `Que, el señor(a) ${persona.nombre.toUpperCase()} ${persona.apellido.toUpperCase()}, identificado con Documento Nacional de Identidad N° ${persona.docIdentidad}, quien presta servicios en esta Corte Superior de Justicia, asignado al Registro Distrital Judicial, en la búsqueda de legajos en el área de Recursos Humanos, y de lo informado por la Secretaría Técnica de Procedimientos Administrativos Disciplinarios: `;

        doc.text(mensajePrincipal, marginX, 88, {
            maxWidth: maxWidth,
            align: "justify",
            lineHeightFactor: 1.5
        });

        // Párrafo de NO REGISTRA (En negrita para resaltar según formato)
        doc.setFont("helvetica", "bold");
        const noRegistra = "NO REGISTRA Sanción Disciplinaria Administrativa alguna.";
        doc.text(noRegistra, marginX, 125, {
            maxWidth: maxWidth,
            align: "justify"
        });

        // 5. Cierre
        doc.setFont("helvetica", "normal");
        doc.text("Se expide el presente documento a solicitud del interesado (a) para los fines que estime conveniente.", marginX, 140, {
            maxWidth: maxWidth,
            align: "justify"
        });

        const fechaActual = `Trujillo, ${new Date().toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}`;
        doc.text(fechaActual, marginX, 185);

        doc.save(`Constancia_Simple_${persona.docIdentidad}.pdf`);
    };

    const buscarYGenerarSimple = async () => {
        if (!filtro.trim()) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'Ingrese DNI o Nombre' });
            return;
        }

        setLoading(true);
        try {
            // Usamos tu endpoint de personas
            const res = await axiosInstance.get("persona/", {
                params: { search: filtro, page: 0, size: 1 }
            });

            const persona = res.data.content[0];
            if (persona) {
                generarConstanciaSimple(persona);
                toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Constancia generada' });
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'Persona no encontrada en el maestro' });
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Error al conectar con el servidor' });
        } finally {
            setLoading(false);
        }
    };

    const handleDescarga = () => {
        if (!resultados || resultados.length === 0) {
            // Caso: No se encontraron resultados (Generar constancia negativa)
            // Necesitas tener los datos de la persona buscada o pedirlos
            toast.current.show({ severity: 'info', summary: 'Info', detail: 'Generando constancia de No Registro' });
            // Simulación de objeto persona para el reporte negativo
            generarPDF({ nombre: filtro, apellido: '', docIdentidad: 'S/D' }, []);
            return;
        }

        // Agrupar por persona para generar un PDF por cada una
        const agrupados = resultados.reduce((acc, current) => {
            const dni = current.persona.docIdentidad;
            if (!acc[dni]) acc[dni] = { persona: current.persona, expedientes: [] };
            acc[dni].expedientes.push(current);
            return acc;
        }, {});

        Object.values(agrupados).forEach(grupo => {
            generarPDF(grupo.persona, grupo.expedientes);
        });
    };



    return (
        <div className="pad-consulta-container">
            <Toast ref={toast} />
            <PageHeader
                title="Consulta de Antecedentes PAD"
                description="Búsqueda de procesos administrativos por documento o nombres"
                icon="pi pi-search-plus"
            />

            {/* Panel de Búsqueda */}
            <Card className="mt-4 shadow-2">
                <form onSubmit={realizarBusqueda} className="flex flex-column md:flex-row gap-3 align-items-end">
                    <div className="flex-grow-1">
                        <label htmlFor="filtroBusqueda" className="block font-bold mb-2">Criterio de Búsqueda</label>
                        <span className="p-input-icon-left w-full">
                            <InputText
                                id="filtroBusqueda"
                                value={filtro}
                                onChange={(e) => setFiltro(e.target.value)}
                                placeholder="DNI, Nombres o Apellidos..."
                                className="w-full"
                                autoFocus
                            />
                        </span>
                    </div>
                    <div className="flex gap-2">
                        <Button
                            label="Buscar"
                            icon="pi pi-search"
                            type="submit"
                            loading={loading}
                            style={{ backgroundColor: '#8E191E', color: '#ffffff', borderColor: '#8E191E' }}
                        />
                        <Button
                            label="Limpiar"
                            icon="pi pi-filter-slash"
                            className="p-button-outlined p-button-secondary"
                            onClick={limpiarBusqueda}
                            type="button"
                            style={{ color: '#6c757d', fontSize: '0.9rem' }}
                        />
                        {/* Lógica Condicional para los Botones de Descarga */}
                        {resultados && resultados.length > 0 ? (
                            // Se encontraron procesos PAD (Botón de grupo)
                            <Button
                                label="Descargar Constancias"
                                icon="pi pi-file-pdf"
                                className="p-button-help p-button-outlined fadein"
                                onClick={handleDescarga}
                                disabled={loading}
                            />
                        ) : (
                            // No hay resultados o aún no se ha buscado (Botón Simple)
                            // Se puede añadir una condición extra para que solo aparezca si resultados === []
                            resultados !== null && (
                                <Button
                                    label="Constancia Simple"
                                    icon="pi pi-file"
                                    onClick={buscarYGenerarSimple}
                                    type="button"
                                    className="p-button-help p-button-outlined fadein"
                                    tooltip="Genera constancia de no registro (Negativa)"
                                    disabled={loading}
                                />
                            )
                        )}
                    </div>
                </form>
            </Card>

            {/* Zona de Resultados */}
            <div className="mt-4">
                {loading && <div className="text-center"><i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i></div>}

                {!loading && resultados !== null && (
                    resultados.length > 0 ? (
                        <div className="fadein">
                            <h3 className="mb-3 text-primary"><i className="pi pi-list mr-2"></i>Registros Encontrados</h3>
                            <DataTable value={resultados} className="shadow-2 border-round overflow-hidden" responsiveLayout="scroll">
                                <Column field="expediente" header="N° Expediente" className="font-bold" />
                                <Column header="Persona" body={personaBody} />
                                <Column field="sancion" header="Sanción Impuesta" />
                                <Column
                                    field="estado"
                                    header="Estado"
                                    body={(rowData) => (
                                        <span className={`customer-badge status-${rowData.estado.toLowerCase()}`}>
                                            {rowData.estado}
                                        </span>
                                    )}
                                />
                                <Column
                                    header="Fecha"
                                    body={(rowData) => new Date(rowData.fecRegistro).toLocaleDateString()}
                                />
                            </DataTable>
                        </div>
                    ) : (
                        <div className="text-center py-5 fadein">
                            <Message
                                severity="info"
                                text="La persona solicitada no tiene casos registrados en el sistema PAD."
                                className="w-full justify-content-start"
                            />
                            <div className="mt-4">
                                <i className="pi pi-folder-open text-300" style={{ fontSize: '5rem' }}></i>
                            </div>
                        </div>
                    )
                )}
            </div>
        </div>
    );
};

export default PADConsulta;